import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CleanupAdvice, CleanupRow, Lifecycle } from '@teo/shared';
import { api } from '../api/client';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { EmptyState, TableSkeleton } from '../components/States';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

const LIFECYCLES: Lifecycle[] = ['ACTIVA', 'AGOTANDOSE', 'TEMPORAL', 'DESCONTINUADA', 'ARCHIVADA'];
const WINDOWS = ['90', '180', '365', '0'] as const;
const TONE: Record<CleanupAdvice, 'red' | 'amber' | 'blue' | 'green' | undefined> = {
  archive: 'red', liquidate: 'amber', watch: 'blue', keep: 'green', archived: undefined,
};

/**
 * Vista "Depurar" (Etapa 3): el negocio crea muchas referencias y algunas no se vuelven a comprar ni a
 * vender, pero siguen alertando en el semáforo. Aquí se encuentran y se archivan en lote, con su motivo.
 * Archivar no borra: la referencia sale de la operación y conserva ventas, costos, Kardex y auditoría.
 */
export function Cleanup() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();

  const [days, setDays] = useState<string>('180');
  const [term, setTerm] = useState('');
  const [state, setState] = useState<string>('');
  const [stock, setStock] = useState<string>('');
  const [selected, setSelected] = useState<number[]>([]);
  const [pending, setPending] = useState<{ lifecycle: Lifecycle; reason: string } | null>(null);

  const board = useLoad(() => api.cleanupBoard({
    days: Number(days),
    q: term.trim() || undefined,
    lifecycle: state || undefined,
    stock: stock || undefined,
  }), [days, term, state, stock]);

  const rows = useMemo(() => board.data?.rows ?? [], [board.data]);
  const counts = board.data?.counts ?? {};
  const money = (n: number) => formatMoney(n, lang);
  const num = (n: number) => formatNumber(n, lang);
  const day = (v: string | null, fallback: string) => (v ? formatDate(v, lang, false) : fallback);

  const advised = useMemo(() => rows.filter((r) => r.recommendation === 'archive').map((r) => r.product_id), [rows]);
  const toggle = (id: number) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  /** Por qué se recomienda eso, con las cifras de la propia referencia. */
  const why = (r: CleanupRow) => {
    if (r.recommendation === 'archive') {
      return r.last_movement === null
        ? t('cleanup.why.archiveNever', { days: num(r.quiet_days) })
        : t('cleanup.why.archive', { days: num(r.quiet_days) });
    }
    if (r.recommendation === 'liquidate') return t('cleanup.why.liquidate', { units: num(r.units), days: num(r.quiet_days) });
    return t(`cleanup.why.${r.recommendation}`);
  };

  const ask = (lifecycle: Lifecycle) => {
    if (selected.length === 0) {
      show(t('cleanup.noneSelected'));
      return;
    }
    setPending({ lifecycle, reason: '' });
  };

  const apply = async () => {
    if (!pending) return;
    const scope = `depurar:${pending.lifecycle}:${selected.join('-')}`;
    await run(async () => {
      try {
        const r = await api.cleanupBulk(requestKey(scope), { product_ids: selected, lifecycle: pending.lifecycle, reason: pending.reason });
        releaseKey(scope);
        show(t('cleanup.done', { count: num(r.count) }));
        setSelected([]);
        setPending(null);
        await board.reload();
      } catch (e) {
        showError(e);
      }
    });
  };

  return (
    <>
      <div className="kpis" id="cleanupKpis" style={{ marginBottom: 12 }}>
        <div className="kpi">
          <div className="lab">{t('cleanup.kpi.active')}</div>
          <div className="val" id="cActive">{num(counts.ACTIVA ?? 0)}</div>
          <div className="sub">{t('cleanup.kpi.activeSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('cleanup.kpi.depleting')}</div>
          <div className="val" id="cDepleting">{num(counts.AGOTANDOSE ?? 0)}</div>
          <div className="sub">{t('cleanup.kpi.depletingSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('cleanup.kpi.discontinued')}</div>
          <div className="val" id="cDiscontinued">{num(counts.DESCONTINUADA ?? 0)}</div>
          <div className="sub">{t('cleanup.kpi.discontinuedSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('cleanup.kpi.archived')}</div>
          <div className="val" id="cArchived">{num(counts.ARCHIVADA ?? 0)}</div>
          <div className="sub">{t('cleanup.kpi.archivedSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('cleanup.kpi.candidates')}</div>
          <div className="val" id="cCandidates">{num(board.data?.candidates ?? 0)}</div>
          <div className="sub">{t('cleanup.kpi.candidatesSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('cleanup.kpi.capital')}</div>
          <div className="val" id="cDiscontinuedCapital">{money(board.data?.discontinued_capital ?? 0)}</div>
          <div className="sub">{t('cleanup.kpi.capitalSub')}</div>
        </div>
      </div>

      <Panel
        title={t('cleanup.title')}
        description={t('cleanup.help')}
        badge={<Badge tone="blue">{t(`cleanup.days.${days}`)}</Badge>}
      >
        <div className="actions" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          <input className="input" id="cleanupSearch" style={{ flex: 1, minWidth: 220 }} placeholder={t('cleanup.search')}
            value={term} onChange={(e) => setTerm(e.target.value)} aria-label={t('cleanup.search')} />
          <select className="select" id="cleanupStatus" value={state} onChange={(e) => setState(e.target.value)} aria-label={t('cleanup.cols.state')}>
            <option value="">{t('cleanup.allStates')}</option>
            {LIFECYCLES.map((l) => <option key={l} value={l}>{t(`lifecycle.${l}`)}</option>)}
          </select>
          <select className="select" id="cleanupStock" value={stock} onChange={(e) => setStock(e.target.value)} aria-label={t('cleanup.cols.stock')}>
            <option value="">{t('cleanup.allStock')}</option>
            <option value="zero">{t('cleanup.stockZero')}</option>
            <option value="with">{t('cleanup.stockWith')}</option>
          </select>
          <select className="select" id="cleanupDays" value={days} onChange={(e) => setDays(e.target.value)} aria-label={t('cleanup.cols.lastMovement')}>
            {WINDOWS.map((d) => <option key={d} value={d}>{t(`cleanup.days.${d}`)}</option>)}
          </select>
        </div>

        <div className="actions" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn" id="selectCandidates" disabled={advised.length === 0}
            onClick={() => { setSelected(advised); show(t('cleanup.advisedSelected', { count: num(advised.length) })); }}>
            {t('cleanup.selectAdvised')}
          </button>
          <button type="button" className="btn black" id="bulkArchive" disabled={busy} onClick={() => ask('ARCHIVADA')}>{t('cleanup.archiveSelected')}</button>
          <button type="button" className="btn sm" id="bulkDepleting" disabled={busy} onClick={() => ask('AGOTANDOSE')}>{t('cleanup.markDepleting')}</button>
          <button type="button" className="btn sm" id="bulkDiscontinue" disabled={busy} onClick={() => ask('DESCONTINUADA')}>{t('cleanup.discontinue')}</button>
          <button type="button" className="btn sm" id="bulkActivate" disabled={busy} onClick={() => ask('ACTIVA')}>{t('cleanup.reactivate')}</button>
        </div>

        {board.loading && !board.data ? <TableSkeleton cols={7} rows={5} /> : null}

        {board.data ? (
          rows.length === 0 ? <EmptyState icon="inbox" title={t('cleanup.empty')} /> : (
            <>
              <div className="tablewrap">
                <table>
                  <thead>
                    <tr>
                      <th className="checkcell">✓</th>
                      <th>{t('cleanup.cols.ref')}</th>
                      <th>{t('cleanup.cols.product')}</th>
                      <th>{t('cleanup.cols.state')}</th>
                      <th className="num">{t('cleanup.cols.stock')}</th>
                      <th>{t('cleanup.cols.lastMovement')}</th>
                      <th>{t('cleanup.cols.lastSale')}</th>
                      <th>{t('cleanup.cols.advice')}</th>
                    </tr>
                  </thead>
                  <tbody id="cleanupBody">
                    {rows.map((r) => (
                      <tr key={r.product_id} data-ref={r.ref} data-advice={r.recommendation}>
                        <td className="checkcell">
                          <input type="checkbox" data-cleancheck={r.product_id} checked={selected.includes(r.product_id)}
                            onChange={() => toggle(r.product_id)} aria-label={r.ref} />
                        </td>
                        <td><b>{r.ref}</b></td>
                        <td>{r.product_name}<div className="tiny muted">{[r.brand, r.category].filter(Boolean).join(' · ')}</div></td>
                        <td><Badge tone={r.lifecycle === 'ACTIVA' ? 'green' : r.lifecycle === 'ARCHIVADA' ? 'red' : 'amber'}>{t(`lifecycle.${r.lifecycle}`)}</Badge></td>
                        <td className="num" data-col="stock">
                          {num(r.units)}
                          {r.transit_units > 0 ? <div className="tiny muted">{t('supply.transit', { n: num(r.transit_units) })}</div> : null}
                        </td>
                        <td data-col="lastMovement">{day(r.last_movement, t('cleanup.never'))}</td>
                        <td data-col="lastSale">{day(r.last_sale, t('cleanup.neverSold'))}</td>
                        <td className="cleanupReason" data-col="advice">
                          <Badge tone={TONE[r.recommendation]}>{t(`cleanup.advice.${r.recommendation}`)}</Badge>
                          <div className="tiny muted">{why(r)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="help" id="cleanupCount" style={{ marginTop: 9 }}>
                {t('cleanup.count', { shown: num(rows.length), selected: num(selected.length) })}
                {board.data.total_rows > rows.length ? ` ${t('cleanup.more', { shown: num(rows.length), total: num(board.data.total_rows) })}` : ''}
              </p>
            </>
          )
        ) : null}
      </Panel>

      <Modal open={pending !== null} title={t('cleanup.reasonTitle')} onClose={() => setPending(null)}>
        {pending ? (
          <>
            <p className="help" id="cleanupConfirm">
              {pending.lifecycle === 'ARCHIVADA'
                ? t('cleanup.confirmArchive', { count: num(selected.length) })
                : t('cleanup.confirmState', { count: num(selected.length), state: t(`lifecycle.${pending.lifecycle}`) })}
            </p>
            <div className="field" style={{ marginTop: 10 }}>
              <label htmlFor="cleanupReason">{t('cleanup.reasonLabel')}</label>
              <input className="input" id="cleanupReason" value={pending.reason} placeholder={t('cleanup.reasonPlaceholder')}
                onChange={(e) => setPending({ ...pending, reason: e.target.value })} />
            </div>
            <div className="actions" style={{ marginTop: 12 }}>
              <button type="button" className="btn black" id="cleanupApply" disabled={busy || pending.reason.trim().length < 3} onClick={apply}>
                {t('cleanup.confirm')}
              </button>
              <button type="button" className="btn sm" id="cleanupCancel" onClick={() => setPending(null)}>{t('cleanup.cancel')}</button>
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
}
